/* eslint-disable no-nested-ternary */
import { Fragment, useEffect, useState } from "react";
import classes from "./CollectionRail.module.scss";
import CompactInventoryCard from "@Components/CompactInventoryCard";
import DefaultCompactInventoryCard from "@Pages/Home/components/HomepageCollectionSkeleton/DefaultCompactInventoryCard";
import Button from "@Components/Button";
import HorizontalScroll from "@Components/HorizontalScroll";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { PAGE, UAL_SALES_BANNER_PARTNER_IDS } from "constants/index";
import { useHistory } from "hooks/useHistory";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import customHistoryPush from "@Utils/customHistoryPush";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import Chevron from "@Icons/Chevron";
import { getWhiteLabelSearchRedirectionURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import getUserLanguageCSR from "helpers/getUserLanguageCSR";
import useIsApp from "hooks/useIsApp";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";

// Define proper TypeScript interfaces
interface Pill {
  name: string;
  canonical_name?: string;
  canonicalName?: string;
  name_zh?: string;
}

interface CollectionItem {
  id: string;
  [key: string]: any;
}

interface CollectionRailProps {
  collection: CollectionItem[];
  pills: Pill[];
  fetchData?: any;
  title: string;
  subTitle?: string;
  showSeeMoreCard?: boolean;
  greyBg?: boolean;
  gtagEventAction?: string;
  gtagEventActionPill?: string | null;
  gtagEventCategory?: string;
  recentlyViewedCityCanonical?: string;
  subSectionPaddingReduced?: boolean;
  isDesktop?: boolean;
  isShowPills?: boolean;
  id?: string;
  showSeeAll?: boolean;
  housingType?: string;
}

const CollectionRail: React.FC<CollectionRailProps> = ({
  collection = [],
  pills = [],
  fetchData,
  title,
  subTitle,
  showSeeMoreCard = true,
  greyBg = false,
  gtagEventAction = "inventory_clicked",
  gtagEventActionPill = null,
  gtagEventCategory = PAGE.HOME,
  recentlyViewedCityCanonical = "",
  subSectionPaddingReduced,
  isDesktop = false,
  isShowPills = true,
  id = "",
  showSeeAll = false,
  housingType = "",
}) => {
  const {
    isWhitelabel,
    whitelabel_region_canonical_name,
    whitelabelConfig,
    isDemoSubdomain,
    partner_uuid,
    partner_id,
  } = useWhitelabel();
  const { t } = useTranslationHook();
  const history = useHistory();
  const userLang = getUserLanguageCSR();

  const isApp = useIsApp();
  const [activePill, setActivePill] = useState<string | null>(pills?.[0]?.name ?? null);
  const [activePillCanonical, setActivePillCanonical] = useState<string | null>(
    pills?.[0]?.canonical_name || pills?.[0]?.canonicalName || null,
  );
  const [isRequestActive, setIsRequestActive] = useState(false);
  const [collectionState, setCollectionState] = useState<CollectionItem[] | "exploreMore">(() => {
    if (!showSeeMoreCard || !collection) return collection;
    return collection.length < 10 ? [...collection, collection[0]] : collection;
  });

  const displayCollection =
    isDesktop || !showSeeMoreCard
      ? collectionState
      : Array.isArray(collectionState)
      ? collectionState.slice(0, 5)
      : collectionState;

  const handleExploreMore = () => {
    try {
      if (isWhitelabel) {
        const redirectionURL = getWhiteLabelSearchRedirectionURL(
          whitelabelConfig,
          whitelabel_region_canonical_name,
          isDemoSubdomain,
          partner_uuid,
          housingType,
        );
        customHistoryPush(history, redirectionURL);
        return;
      }

      const recentlyVisited = JSON.parse(localStorage.getItem("recentlyVisited") ?? "{}");
      const searchPath = `/places/search/${
        activePillCanonical ?? recentlyVisited?.[0]?.canonical_name ?? recentlyViewedCityCanonical
      }`;

      customHistoryPush(history, searchPath);
    } catch (error) {
      console.error("Error in handleExploreMore:", error);
      // Fallback to default search path
      customHistoryPush(history, "/places/search");
    }
  };

  const fetchCityUniData = async (pill: Pill) => {
    if (!pill) return;

    try {
      setIsRequestActive(true);

      if (gtagEventActionPill) {
        gtagEventEmitter({
          action: gtagEventActionPill,
          event_category: PAGE.HOME,
          label: pill.name,
        });
      }
      setActivePill(pill.name);
      setActivePillCanonical(pill.canonical_name || pill.canonicalName || null);
      if (fetchData) {
        const response = await fetchData(pill);
        const results = response?.result ?? [];

        if (results.length === 0) {
          setCollectionState("exploreMore");
        } else if (results.length < 10 && showSeeMoreCard) {
          setCollectionState([...results, results[0]]);
        } else {
          setCollectionState(results);
        }
      }
    } catch (error) {
      console.error("Error fetching city data:", error);
      setCollectionState("exploreMore");
    } finally {
      setIsRequestActive(false);
    }
  };

  useEffect(() => {
    if (!isShowPills) {
      setActivePill("Recently Viewed");
    }
  }, [isShowPills]);

  // Render helpers
  const renderPills = () => {
    if (isWhitelabel || !isShowPills || pills.length <= 1) return null;

    return (
      <HorizontalScroll
        className={formatClassNames(classes.horizontalScrollCr, isDesktop && classes.desktopCr)}
        bgColor="white"
      >
        {pills.map((pill, index) =>
          pill?.name || pill?.canonical_name ? (
            <div
              key={`${pill.name}-${index}`}
              role="button"
              tabIndex={0}
              onClick={() => fetchCityUniData(pill)}
              className={formatClassNames(
                pill.name === activePill ? classes.pillsActive : classes.pills,
                isDesktop && classes.pillsDesktop,
              )}
              data-testid={`homepage-${pill.name}-pills`}
              aria-label={pill.name}
              onKeyDown={(event) => event.key === "Enter" && fetchCityUniData(pill)}
            >
              {userLang === "zh" ? pill.name_zh || t(pill.name) : t(pill.name)}
            </div>
          ) : (
            <Fragment key={index} />
          ),
        )}
      </HorizontalScroll>
    );
  };

  const renderContent = () => {
    if (isRequestActive) {
      return (
        <HorizontalScroll
          className={formatClassNames(classes.horizontalScrollCr, isDesktop && classes.desktopCr)}
        >
          {Array.from({ length: 10 }).map((_, index) => (
            <DefaultCompactInventoryCard key={index} />
          ))}
        </HorizontalScroll>
      );
    }

    if (collectionState === "exploreMore") {
      return (
        <div className={classes.explorePropertiesButtonContainer}>
          <Button
            onClick={handleExploreMore}
            variant={isWhitelabel ? "whiteLabel_primary" : "primary"}
            type="button"
            className={classes.explorePropertiesButton}
            dataTestId="explore-properties-button"
          >
            {t("home.components.thousandpropertiesnewrails.exploremoreproperties")}
          </Button>
        </div>
      );
    }

    if (Array.isArray(collectionState) && collectionState.length > 0) {
      return (
        <HorizontalScroll
          dataTestId={title}
          showControls={isDesktop}
          className={formatClassNames(classes.horizontalScrollCr, isDesktop && classes.desktopCr)}
          bgColor="white"
        >
          {Array.isArray(displayCollection) &&
            displayCollection.map((item, i) => {
              const isLastCard = i === displayCollection.length - 1;

              return (
                <CompactInventoryCard
                  dataTestId={title}
                  item={item}
                  key={`${item.id}-${i}`}
                  isRequestActive={isRequestActive}
                  parentCanonicalName={isLastCard ? activePillCanonical : null}
                  {...(isLastCard &&
                    housingType?.length > 0 &&
                    isWhitelabel && {
                      link: getWhiteLabelSearchRedirectionURL(
                        whitelabelConfig,
                        whitelabel_region_canonical_name,
                        isDemoSubdomain,
                        partner_uuid,
                        housingType,
                      ),
                    })}
                  showIcons
                  gtagEventAction={gtagEventAction}
                  gtagEventCategory={gtagEventCategory}
                  isDesktop={isDesktop}
                  showHappyFlowCTA={isApp && activePill === "Active Enquiries"}
                  screenName={APP_PAGE.HOME}
                  showUalSalesBanner={
                    isWhitelabel &&
                    UAL_SALES_BANNER_PARTNER_IDS.includes(Number(partner_id)) &&
                    housingType === "university_provider" &&
                    Number(item.id) === 3282580
                  }
                />
              );
            })}
        </HorizontalScroll>
      );
    }

    return null;
  };

  return (
    <>
      <div
        data-testid={title}
        className={formatClassNames(
          !isDesktop && subSectionPaddingReduced ? classes.collectionRailPaddingReduced : {},
          classes.sectionContainer,
          greyBg ? classes.greyBg : {},
          isDesktop && classes.desktop,
          !isShowPills && classes.withOutPills,
        )}
        id={id}
      >
        <div className={isDesktop ? classes.titleBarDesktop : classes.titleBar}>
          <div className={classes.titleBarContainer}>
            <h2
              className={formatClassNames(
                isWhitelabel ? "wl-title-font" : "",
                classes.sectionTitle,
              )}
            >
              {t(title)}
            </h2>
            {showSeeAll && isWhitelabel && isDesktop && (
              <div
                className={classes.seeAllBtn}
                role="button"
                onClick={handleExploreMore}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleExploreMore()}
              >
                {t("whitelabel.home.collections.seeall")}
                <div className={classes.chevron}>
                  <Chevron height="12" width="12" />
                </div>
              </div>
            )}
          </div>
          {subTitle && <h5 className={classes.sectionSubTitle}>{t(subTitle)}</h5>}
        </div>
        <div
          className={formatClassNames(
            classes.carouselPart,
            isDesktop && classes.carouselPartDesktop,
          )}
        >
          {renderPills()}
          {renderContent()}
        </div>
      </div>
      <div
        className={formatClassNames(classes.bottomBorder, isDesktop && classes.bottomBorderDesktop)}
      />
    </>
  );
};

export default wrapperHOC(CollectionRail, {
  componentName: "CollectionRail-Home",
  showForChina: true,
});
